import { useEffect, useRef } from 'react';
import { signaling } from './ws';

// App-only staleness stop-gap for screens that fetch once on mount + on pull-to-refresh and
// otherwise have no poll/push for their own payload (subscription list, device list, device
// subscription detail). Server-side expiry teardown flips record_mode / subscription status,
// but these REST reads would only pick it up on the next manual refresh. So we re-pull once
// when the app comes back to the FOREGROUND (tab visible / window focused) and once on WSS
// RECONNECT — the two moments where the user is looking again and the data may have drifted.
//
// Deliberately NOT a poll and NOT live-while-open: the owner accepts convergence on
// foreground/reconnect (matches the iOS .task + .refreshable screens getting the same treatment).
//
// Debounced (trailing edge) so the focus+visibilitychange pair a single tab switch fires does
// not double-refetch, and so a flurry of focus toggles coalesces into one request. The mount
// fetch is the caller's own effect and never routes through here, so it is never duplicated.
const FOREGROUND_REFETCH_DEBOUNCE_MS = 800;

/**
 * Call `refetch` once when the app returns to the foreground (visibilitychange → visible /
 * window focus) and once on WSS reconnect (signaling 'state' → 'open' AFTER a 'reconnecting'
 * spell — the initial connect goes connecting→open and is skipped so it never races the mount
 * fetch). Latest `refetch` is always used; all listeners are removed on unmount.
 */
export function useRefetchOnForeground(refetch: () => void): void {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const fire = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        refetchRef.current();
      }, FOREGROUND_REFETCH_DEBOUNCE_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fire();
    };
    const onFocus = () => fire();

    // Only a reconnect (reconnecting → open) refetches; the first connect (connecting → open)
    // coincides with the mount fetch, so gate on the previous state being 'reconnecting'.
    let prevState = signaling.getState();
    const offState = signaling.on('state', (s) => {
      const wasReconnecting = prevState === 'reconnecting';
      prevState = s;
      if (s === 'open' && wasReconnecting) fire();
    });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      offState();
    };
  }, []);
}
